import { RouterProvider, type RouterHistory } from '@tanstack/react-router'
import {
  Activity,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'

import {
  OverlayHostContext,
  WindowFrame,
  startPointerDrag,
  type OverlayHost,
  type ResizeEdge,
} from '@aeris/ui'

import {
  maximizedRect,
  resizeRect,
  type Rect,
} from '../../lib/desktop/geometry.ts'
import { windowOwnsKey } from '../../lib/desktop/keyboard-ownership.ts'
import { useWindowRouterFactory } from '../../lib/desktop/window-router.ts'
import {
  useDesktopSelector,
  windowLabel,
  type DesktopStore,
} from '../../lib/desktop/window-store.ts'
import { MODULES } from '../../lib/modules.tsx'
import { ShellFrameContext, type ShellFrame } from '../../lib/shell-frame.ts'
import { moduleForPath, moduleTitle } from '../../lib/workspace.ts'

/**
 * Uma janela da área de trabalho do Modo PC — F138.
 *
 * ## O que ela é
 *
 * A MESMA tela do navegador, num roteador só dela (`createMemoryHistory`) e
 * dentro de uma moldura que arrasta. `Link`, `useNavigate` e `useSearch` da
 * tela resolvem para este roteador; nenhuma tela sabe que está numa janela
 * (Master Specification §26).
 *
 * ## O que ela não faz
 *
 * Redesenhar a tela quando a janela muda de lugar. A geometria vem do store com
 * seletor por janela, e o conteúdo é um elemento memoizado: ganhar foco,
 * subir na pilha ou ser arrastada não re-renderiza a lista de produtos lá
 * dentro. Arrastar escreve `transform` direto no elemento e só grava a posição
 * ao soltar; redimensionar desenha um contorno e só muda o tamanho de verdade
 * ao soltar — relayout de uma tabela grande a cada pixel é o que trava um
 * Celeron.
 *
 * ## As janelas filhas
 *
 * Um diálogo aberto pela tela — a ficha do produto, o recebimento do PDV —
 * é desenhado na camada desta janela (`OverlayHost.layer`), por cima dela e com
 * o mesmo lugar na pilha: subir a janela sobe a filha junto, minimizar esconde
 * as duas. Enquanto houver filha, a tela fica bloqueada (`inert` e um véu), e só
 * ela: as outras janelas continuam usáveis.
 *
 * ## Minimizada
 *
 * Dentro de `<Activity mode="hidden">`: o React guarda o estado — o formulário
 * pela metade, a linha selecionada — e desliga os efeitos: listeners de
 * teclado, consultas, a conexão ao vivo do Atendimento. Restaurar liga de novo.
 */

/** Os caminhos que não moram dentro do shell. */
const OUTSIDE_SHELL: readonly string[] = [
  '/login',
  '/trocar-senha',
  '/servidor',
]

const place = (element: HTMLElement, rect: Rect): void => {
  element.style.left = `${String(rect.x)}px`
  element.style.top = `${String(rect.y)}px`
  element.style.width = `${String(rect.w)}px`
  element.style.height = `${String(rect.h)}px`
}

export type WindowHostProps = {
  readonly store: DesktopStore
  readonly id: string
  /** Registra a navegação da janela, para abrir um registro nela depois. */
  readonly onHistory: (id: string, history: RouterHistory | null) => void
  /** Quantas janelas filhas estão abertas sobre esta. */
  readonly onChildren: (id: string, count: number) => void
  /** A tela mandou para fora do shell: login, trocar senha, servidor. */
  readonly onLeaveShell: (id: string, href: string) => void
}

export const WindowHost = memo(
  ({
    store,
    id,
    onHistory,
    onChildren,
    onLeaveShell,
  }: WindowHostProps): ReactNode => {
    const current = useDesktopSelector(store, (state) =>
      state.windows.find((entry) => entry.id === id)
    )
    const active = useDesktopSelector(store, (state) => state.activeId === id)
    const bounds = useDesktopSelector(store, (state) => state.bounds)

    const makeRouter = useWindowRouterFactory()
    const [{ router, history }] = useState(() =>
      makeRouter(
        store.getState().windows.find((entry) => entry.id === id)?.href ??
          '/inicio'
      )
    )

    const frameRef = useRef<HTMLDivElement>(null)
    const ghostRef = useRef<HTMLDivElement>(null)
    /** A camada das janelas filhas. O conteúdo só existe depois dela. */
    const [layer, setLayer] = useState<HTMLDivElement | null>(null)
    const [children, setChildren] = useState(0)
    const childrenRef = useRef(0)
    const previousChildren = useRef(0)

    useEffect(() => {
      childrenRef.current = children
    })

    useEffect(() => {
      onHistory(id, history)
      return () => {
        onHistory(id, null)
      }
    }, [id, history, onHistory])

    useEffect(() => {
      onChildren(id, children)
    }, [id, children, onChildren])

    /*
     * A última filha fechou e o foco caiu no corpo da página (o botão que a
     * abriu estava bloqueado quando ela saiu): devolve o foco para a tela, senão
     * o próximo atalho não sabe de quem é.
     */
    useEffect(() => {
      const had = previousChildren.current
      previousChildren.current = children
      if (had === 0 || children > 0) return
      if (document.activeElement !== document.body) return
      frameRef.current
        ?.querySelector<HTMLElement>('[data-window-content]')
        ?.focus()
    }, [children])

    /*
     * O título acompanha a tela: um link de Produtos para Preços dentro da
     * janela muda o que a barra de título e a barra de tarefas dizem.
     */
    useEffect(
      () =>
        router.subscribe('onResolved', ({ toLocation }) => {
          if (OUTSIDE_SHELL.includes(toLocation.pathname)) {
            onLeaveShell(id, toLocation.href)
            return
          }
          const module = moduleForPath(toLocation.pathname)
          store.dispatch({
            type: 'navigated',
            id,
            href: toLocation.href,
            // Uma tela sem módulo (`/plataforma`) guarda o título com que abriu.
            title:
              module === null
                ? (store.getState().windows.find((entry) => entry.id === id)
                    ?.title ?? 'Aeris One')
                : moduleTitle(module),
            module,
          })
        }),
      [router, store, id, onLeaveShell]
    )

    const frame = useMemo<ShellFrame>(
      () => ({ kind: 'window', windowId: id }),
      [id]
    )

    const host = useMemo<OverlayHost>(
      () => ({
        layer,
        isActive: () => store.getState().activeId === id,
        hold: () => {
          setChildren((count) => count + 1)
          return () => {
            setChildren((count) => count - 1)
          }
        },
        // Com uma filha aberta a tela está bloqueada, e a tecla é da filha.
        ownsKeyboard: (event) =>
          childrenRef.current === 0 &&
          windowOwnsKey({
            active: store.getState().activeId === id,
            frame: frameRef.current,
            target: event.target,
          }),
      }),
      [store, id, layer]
    )

    /*
     * O conteúdo, memoizado: é o que impede que focar, empilhar ou arrastar a
     * janela redesenhe a tela inteira que está dentro dela. Só depois que a
     * camada das filhas existe — senão um diálogo aberto na primeira pintura
     * (`/pessoas?editar=`) não teria onde se conter e cobriria a página.
     */
    const content = useMemo(
      () =>
        layer === null ? null : (
          <ShellFrameContext value={frame}>
            <OverlayHostContext value={host}>
              <RouterProvider router={router} />
            </OverlayHostContext>
          </ShellFrameContext>
        ),
      [frame, host, router, layer]
    )

    if (current === undefined) return null

    const focus = (): void => {
      store.dispatch({ type: 'focus', id })
    }

    const focusChild = (): void => {
      const dialogs = layer?.querySelectorAll<HTMLElement>(
        '[data-contained-dialog]'
      )
      dialogs?.[dialogs.length - 1]?.focus()
    }

    const onTitlePointerDown = (
      event: ReactPointerEvent<HTMLDivElement>
    ): void => {
      const element = frameRef.current
      const start = store.getState().windows.find((entry) => entry.id === id)
      if (element === null || start === undefined) return

      focus()
      startPointerDrag(event, {
        onMove: ({ dx, dy }) => {
          element.style.transform = `translate3d(${String(dx)}px, ${String(dy)}px, 0)`
        },
        onEnd: ({ dx, dy }) => {
          store.dispatch({
            type: 'move',
            id,
            x: start.rect.x + dx,
            y: start.rect.y + dy,
          })
          /*
           * O `transform` sai só no quadro seguinte, depois que o React já
           * escreveu a posição nova. Tirado antes, a janela piscaria de volta
           * para onde estava por um quadro.
           */
          requestAnimationFrame(() => {
            element.style.transform = ''
          })
        },
        onCancel: () => {
          element.style.transform = ''
        },
      })
    }

    const onResizePointerDown = (
      edge: ResizeEdge,
      event: ReactPointerEvent<HTMLDivElement>
    ): void => {
      const ghost = ghostRef.current
      const start = store.getState().windows.find((entry) => entry.id === id)
      if (ghost === null || start === undefined) return

      focus()
      place(ghost, start.rect)
      ghost.hidden = false
      startPointerDrag(event, {
        onMove: ({ dx, dy }) => {
          place(ghost, resizeRect(start.rect, edge, dx, dy))
        },
        onEnd: ({ dx, dy }) => {
          ghost.hidden = true
          store.dispatch({
            type: 'resize',
            id,
            rect: resizeRect(start.rect, edge, dx, dy),
          })
        },
        onCancel: () => {
          ghost.hidden = true
        },
      })
    }

    const minimized = current.mode === 'minimized'
    const maximized = current.mode === 'maximized'
    const blocked = children > 0
    const rect = maximized ? maximizedRect(bounds) : current.rect
    const Icon = MODULES.find((entry) => entry.module === current.module)?.Icon

    return (
      <>
        {/*
         * A janela e as filhas dela sobem e descem juntas: um grupo só na
         * pilha da área de trabalho. O grupo não pega clique (`pointer-events-
         * none`); a moldura e cada filha pegam.
         */}
        <div
          data-desktop-group=""
          hidden={minimized}
          // Clicar ou pôr o foco em qualquer ponto da janela ou de uma filha a
          // traz para a frente — inclusive o Tab que entra vindo de outra.
          onPointerDownCapture={focus}
          onFocusCapture={focus}
          className="pointer-events-none absolute inset-0"
          style={{ zIndex: current.z }}
        >
          <WindowFrame
            ref={frameRef}
            title={windowLabel(current)}
            icon={
              Icon === undefined ? undefined : <Icon className="h-3.5 w-3.5" />
            }
            active={active}
            maximized={maximized}
            className="pointer-events-auto"
            style={{
              left: rect.x,
              top: rect.y,
              width: rect.w,
              height: rect.h,
            }}
            onMinimize={() => {
              store.dispatch({ type: 'minimize', id })
            }}
            onToggleMaximize={() => {
              store.dispatch({ type: 'toggleMaximize', id })
            }}
            onClose={() => {
              // Com uma ficha aberta, fechar a janela jogaria fora o que está
              // sendo digitado: o botão leva até a ficha em vez disso.
              if (childrenRef.current > 0) {
                focusChild()
                return
              }
              store.dispatch({ type: 'close', id })
            }}
            onTitlePointerDown={onTitlePointerDown}
            onResizePointerDown={onResizePointerDown}
          >
            <div inert={blocked} className="h-full">
              <Activity mode={minimized ? 'hidden' : 'visible'}>
                {content}
              </Activity>
            </div>
            {blocked ? (
              <div
                aria-hidden="true"
                onPointerDown={focusChild}
                className="absolute inset-0 z-(--z-overlay) bg-(--surface-overlay)"
              />
            ) : null}
          </WindowFrame>

          {/*
           * A camada das janelas filhas desta janela.
           *
           * **O `z-index` não é enfeite.** A moldura e esta camada são irmãs
           * dentro do grupo, as duas sem empilhamento próprio — então qualquer
           * `z-index` positivo do CONTEÚDO da janela passa por cima das duas.
           * O cabeçalho grudento de uma tabela (`TableHead`, `sticky z-10`) é
           * exatamente isso: sem esta linha ele atravessava a ficha aberta, e
           * "CLIENTE TIPO DEVOLVIDO VALE" aparecia escrito por cima do
           * formulário de troca. O véu de bloqueio logo acima tinha o mesmo
           * furo, e pela mesma razão.
           *
           * Os números são os mesmos tokens do diálogo de página, e aqui valem
           * dentro do grupo desta janela: `--z-dropdown` (50) é o teto do que o
           * conteúdo alcança, o véu vem depois e a filha por último.
           */}
          <div
            ref={setLayer}
            data-window-layer=""
            className="pointer-events-none absolute inset-0 z-(--z-dialog)"
          />
        </div>

        {/* O contorno de redimensionar: o tamanho real muda ao soltar. */}
        <div
          ref={ghostRef}
          hidden
          aria-hidden="true"
          className="pointer-events-none absolute rounded-(--radius-md) border-2 border-dashed border-(--window-border-active)"
          style={{ zIndex: 2_000_000_000 }}
        />
      </>
    )
  }
)

WindowHost.displayName = 'WindowHost'
